// Reads and writes the account_config keys behind the daily routes editor.
// Every save re-reads the stored values first, so edits made elsewhere in the
// meantime survive.
//
// An address edit always lands in route_address_aliases, under its route. It
// also updates address_aliases when every route that has the code gets that
// same address, as the wizard falls back to that one for manifests of routes
// it doesn't know.
import type { Client } from "@xmpp/client"
import AdHocCommands from "@/api/adHocCommands"
import type { RouteChanges } from "./draft"
import { fitsConfigLimit } from "./pythonJson"
import type {
  AddressAliases,
  AliasEntry,
  RouteAddressAliases,
  RouteStopOrder,
  RoutesData,
} from "./types"

// Stored values as they are, before any filtering. Saves merge into these so
// nothing the editor doesn't display is lost.
type RawConfig = {
  aliases: Record<string, unknown>
  routes: Record<string, unknown>
  routeAliases: Record<string, unknown>
}

const ALIASES_KEY = "address_aliases"
const ROUTES_KEY = "route_stop_order"
const ROUTE_ALIASES_KEY = "route_address_aliases"

export class ConfigTooLargeError extends Error {
  constructor() {
    super("Routes data is too large to save")
    this.name = "ConfigTooLargeError"
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const parseAliases = (value: unknown): AddressAliases => {
  const aliases: AddressAliases = {}
  if (!isRecord(value)) return aliases
  Object.entries(value).forEach(([code, entry]) => {
    if (
      isRecord(entry) &&
      typeof entry.address === "string" &&
      entry.address.trim() !== ""
    ) {
      aliases[code] = entry as AliasEntry
    }
  })
  return aliases
}

const parseRoutes = (value: unknown): RouteStopOrder => {
  const routes: RouteStopOrder = {}
  if (!isRecord(value)) return routes
  Object.entries(value).forEach(([route, codes]) => {
    if (!Array.isArray(codes)) return
    routes[route] = codes.filter(
      (code): code is string => typeof code === "string" && code !== "",
    )
  })
  return routes
}

const parseRouteAliases = (value: unknown): RouteAddressAliases => {
  const routes: RouteAddressAliases = {}
  if (!isRecord(value)) return routes
  Object.entries(value).forEach(([route, aliases]) => {
    if (isRecord(aliases)) routes[route] = parseAliases(aliases)
  })
  return routes
}

const parseAll = (stored: {
  aliases: unknown
  routes: unknown
  routeAliases: unknown
}): RoutesData => ({
  aliases: parseAliases(stored.aliases),
  routes: parseRoutes(stored.routes),
  routeAliases: parseRouteAliases(stored.routeAliases),
})

const readStored = async (client: Client) => {
  const [aliases, routes, routeAliases] = await Promise.all([
    AdHocCommands.getAccountConfig(client, ALIASES_KEY),
    AdHocCommands.getAccountConfig(client, ROUTES_KEY),
    AdHocCommands.getAccountConfig(client, ROUTE_ALIASES_KEY),
  ])
  return { aliases, routes, routeAliases }
}

// For saving: an unset value (null) starts as empty, but a stored value of any
// other shape is never replaced, as it is not ours to overwrite.
const loadRawConfig = async (client: Client): Promise<RawConfig> => {
  const stored = await readStored(client)
  const plain = (key: string, value: unknown): Record<string, unknown> => {
    if (value === null || value === undefined) return {}
    if (isRecord(value)) return value
    throw new Error(`Stored ${key} is not an object, refusing to overwrite it`)
  }
  return {
    aliases: plain(ALIASES_KEY, stored.aliases),
    routes: plain(ROUTES_KEY, stored.routes),
    routeAliases: plain(ROUTE_ALIASES_KEY, stored.routeAliases),
  }
}

// The display view: only well-formed entries
export const loadRoutesData = async (client: Client): Promise<RoutesData> =>
  parseAll(await readStored(client))

// A new address invalidates the stored coordinates; other fields stay
const withAddress = (previous: unknown, address: string) => {
  const kept = isRecord(previous) ? { ...previous } : {}
  delete kept.lat
  delete kept.lng
  return { ...kept, address }
}

export const mergeForSave = (
  fresh: RawConfig,
  changes: RouteChanges,
): RawConfig => {
  // an order that was not edited here may be stale, so the stored one stays
  const routes = changes.orderChanged
    ? { ...fresh.routes, [changes.route]: changes.order }
    : fresh.routes
  const newAddress = (route: string, code: string) =>
    changes.addresses.find((a) => a.route === route && a.code === code)?.address
  // the shared address stays only where some route keeps the old one
  const everyRouteGets = (code: string, address: string) =>
    Object.entries(routes).every(
      ([route, codes]) =>
        !Array.isArray(codes) ||
        !codes.includes(code) ||
        newAddress(route, code) === address,
    )
  const aliases: Record<string, unknown> = { ...fresh.aliases }
  const routeAliases: Record<string, unknown> = { ...fresh.routeAliases }
  changes.addresses.forEach(({ route, code, address }) => {
    if (address.trim() === "") {
      throw new Error(`Empty address for stop ${code}`)
    }
    const stored = routeAliases[route]
    const own: Record<string, unknown> = isRecord(stored) ? { ...stored } : {}
    own[code] = withAddress(own[code], address)
    routeAliases[route] = own
    if (everyRouteGets(code, address)) {
      aliases[code] = withAddress(fresh.aliases[code], address)
    }
  })
  return { aliases, routes, routeAliases }
}

const sameJson = (a: unknown, b: unknown) =>
  JSON.stringify(a) === JSON.stringify(b)

export const saveRoute = async (
  client: Client,
  changes: RouteChanges,
): Promise<RoutesData> => {
  const fresh = await loadRawConfig(client)
  const merged = mergeForSave(fresh, changes)
  if (
    !fitsConfigLimit(merged.aliases) ||
    !fitsConfigLimit(merged.routes) ||
    !fitsConfigLimit(merged.routeAliases)
  ) {
    throw new ConfigTooLargeError()
  }
  // Addresses first: a half-finished save then leaves unused addresses, never
  // an order that points at codes without one.
  if (!sameJson(merged.aliases, fresh.aliases)) {
    await AdHocCommands.setAccountConfig(client, ALIASES_KEY, merged.aliases)
  }
  if (changes.addresses.length > 0) {
    await AdHocCommands.setAccountConfig(
      client,
      ROUTE_ALIASES_KEY,
      merged.routeAliases,
    )
  }
  if (changes.orderChanged) {
    await AdHocCommands.setAccountConfig(client, ROUTES_KEY, merged.routes)
  }
  return parseAll(merged)
}
